import {
  CopyIcon,
  EditIcon,
  Drawer,
  Tabs,
  LoadingLoop,
  RefreshIcon,
} from '@medeloop/medeloop-components-library';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';

import {
  getStudyById,
  getStudyOrganizations,
  getStudyParticipants,
  getStudyResearchers,
  regenerateStudyInvitationCode,
} from 'src/api/studies';
import { TooltipButton } from 'src/components';
import { selectAuthToken, selectAccount } from 'src/store/selectors';
import { showToast } from 'src/store/toast';

import { Organization } from 'src/types/organizations';
import { StudyResearcher } from 'src/types/studies';
import { StudyParticipant } from 'src/types/studies';
import { StudyParticipantsTable } from '../tables/study-participants-table';
import { StudyResearchersTable } from '../tables/study-researchers-table';
import { EditStudy } from './study-edit';

type OutletContextType = {
  container: HTMLElement;
};

export const StudyDetails = () => {
  const token = useSelector(selectAuthToken);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useSelector(selectAccount);

  const { studyId } = useParams();

  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [isEdit, setIsEdit] = useState(false);

  if (!studyId) {
    navigate(-1);
  }

  // set container for drawer children
  const [container, setContainer] = useState(document.body);
  const drawerRef = useRef(null);
  useEffect(() => {
    if (drawerRef.current) {
      setContainer(drawerRef.current);
    }
  }, [drawerRef]);

  const studyQuery = useQuery({
    queryKey: ['study', studyId],
    queryFn: async () => {
      if (studyId) {
        const [study, organizations] = await Promise.all([
          getStudyById({ token, id: studyId }),
          getStudyOrganizations({ token, id: studyId }),
        ]);

        return { study: study.study, organizations: organizations.organizations };
      }
    },
  });

  const handleEdit = () => {
    studyQuery.refetch();
    setIsEdit(true);
  };

  const isAdmin =
    studyQuery.data &&
    studyQuery.data.organizations.some((org: Organization) =>
      user.organizationAdminIds.some((id) => org.organizationId.startsWith(id)),
    );

  const isResearcher =
    studyQuery.data &&
    studyQuery.data.organizations.some((org: Organization) =>
      user.organizationIds.some((id) => org.organizationId.startsWith(id)),
    );

  const studyParticipantsQuery = useQuery<{
    participants: StudyParticipant[];
  }>({
    queryKey: ['study', studyId, 'participants'],
    queryFn: async () => {
      if (studyId) {
        return getStudyParticipants({ token, id: studyId });
      }
    },
    enabled: isResearcher,
  });

  const studyResearchersQuery = useQuery<{
    researchers: StudyResearcher[];
  }>({
    queryKey: ['study', studyId, 'researchers'],
    queryFn: async () => {
      if (studyId) {
        return getStudyResearchers({ token, id: studyId });
      }
    },
  });

  const refreshInvitationCodeMutation = useMutation({
    mutationFn: async () => {
      if (studyId) {
        return regenerateStudyInvitationCode({ token, studyId });
      }
    },
    onSuccess: () => {
      dispatch(
        showToast({
          type: 'success',
          message: t('success_invitation_code_refreshed'),
        }),
      );
      studyQuery.refetch();
    },
  });

  // Refetch data when location changes
  // This is useful when navigating back to the page
  const location = useLocation();
  useEffect(() => {
    studyQuery.refetch();
    studyResearchersQuery.refetch();
    studyParticipantsQuery.refetch();
  }, [location]);

  const handleCopyInvitationCode = () => {
    if (studyQuery.data) {
      navigator.clipboard.writeText(studyQuery.data.study.invitationCode);

      dispatch(
        showToast({
          type: 'success',
          message: t('success_invitation_code_copied'),
        }),
      );
    }
  };

  const handleRefreshInvitationCode = () => {
    refreshInvitationCodeMutation.mutate();
  };

  return (
    <Drawer
      open={isDrawerOpen}
      onOpenChange={() => {
        setIsDrawerOpen(!isDrawerOpen);
        setIsEdit(false);
        navigate(-1);
      }}
    >
      <Drawer.Overlay />
      <Drawer.Content className="w-5/6 overflow-auto" ref={drawerRef}>
        {studyQuery.isLoading && (
          <div className="flex flex-grow items-center justify-center">
            <LoadingLoop className="w-16" />
          </div>
        )}

        {studyQuery.isError && !studyQuery.data && (
          <div className="flex flex-grow items-center justify-center">
            <div className="bg-red-200 text-red-600 p-4 rounded-md">{t('error_getting_study')}</div>
          </div>
        )}

        {studyQuery.data && !isEdit && (
          <div className="flex flex-col flex-grow gap-4">
            <Drawer.Header className="!mb-5">
              <div className="flex">
                <div className="flex gap-4 flex-col">
                  <Drawer.Title>{studyQuery.data.study.displayName}</Drawer.Title>
                  <Drawer.Description>{studyQuery.data.study.name}</Drawer.Description>
                </div>

                {/* Actions */}
                <div className="flex flex-grow justify-end gap-2 items-start">
                  <TooltipButton
                    tooltipText={t('copy_invitation_code')}
                    onClick={handleCopyInvitationCode}
                  >
                    <CopyIcon className="h-5 w-5" />
                  </TooltipButton>
                  {isAdmin && (
                    <>
                      <TooltipButton
                        tooltipText={t('refresh_invitation_code')}
                        onClick={handleRefreshInvitationCode}
                      >
                        <RefreshIcon className="h-5 w-5" />
                      </TooltipButton>
                      <TooltipButton tooltipText={t('edit_study')} onClick={() => setIsEdit(true)}>
                        <EditIcon className="h-5 w-5" />
                      </TooltipButton>
                    </>
                  )}
                </div>
              </div>
              {studyQuery.data.organizations[0] && (
                <div className="flex pt-4">
                  <span className="py-2 px-3 border border-yellow-500 text-yellow-500 rounded-3xl text-sm font-semibold">
                    {studyQuery.data.organizations[0].organizationName}
                  </span>
                </div>
              )}
            </Drawer.Header>

            {/* General Information */}
            <div className="flex flex-col gap-4">
              <span className="text-white font-bold text-lg">{t('general_information')}</span>

              <div className="flex bg-gray-900 p-4 text-white rounded-lg gap-16">
                <div className="flex flex-col flex-1 gap-2">
                  <span className="font-semibold">{t('participants_header')}</span>
                  <span className="text-gray-500 text-sm">
                    {studyParticipantsQuery.data?.participants
                      ? studyParticipantsQuery.data.participants.length
                      : 0}
                  </span>
                </div>
                <div className="flex flex-col flex-2 gap-2">
                  <span className="font-semibold">{t('expected_time_in_study')}</span>
                  <span className="text-gray-500 text-sm">
                    {studyQuery.data.study.expectedParticipantDuration}
                  </span>
                </div>
                <div className="flex flex-col flex-2 gap-2">
                  <span className="font-semibold">{t('expected_total_participants')}</span>
                  <span className="text-gray-500 text-sm">
                    {studyQuery.data.study.expectedParticipants}
                  </span>
                </div>
                <div className="flex flex-col flex-1 gap-2">
                  <span className="font-semibold">{t('start_date')}</span>
                  <span className="text-gray-500 text-sm">{studyQuery.data.study.startDate}</span>
                </div>
                <div className="flex flex-col flex-1 gap-2">
                  <span className="font-semibold">{t('completion_date')}</span>
                  <span className="text-gray-500 text-sm">
                    {studyQuery.data.study.completionDate}
                  </span>
                </div>
              </div>
            </div>

            {/* Overview */}
            <div className="flex flex-grow flex-col gap-4">
              <span className="text-white font-bold text-lg">{t('users')}</span>

              <Tabs
                defaultValue={isResearcher ? 'participants' : 'researchers'}
                className="bg-gray-900 text-white rounded-lg"
              >
                <Tabs.List>
                  {isResearcher && (
                    <Tabs.Trigger value="participants">{t('participants_header')}</Tabs.Trigger>
                  )}
                  <Tabs.Trigger value="researchers">{t('researchers_header')}</Tabs.Trigger>
                </Tabs.List>
                {isResearcher && (
                  <Tabs.Content value="participants">
                    <StudyParticipantsTable
                      participants={studyParticipantsQuery?.data?.participants || []}
                    />
                  </Tabs.Content>
                )}
                <Tabs.Content value="researchers">
                  <StudyResearchersTable
                    researchers={studyResearchersQuery?.data?.researchers || []}
                    allowActions={isAdmin}
                  />
                </Tabs.Content>
              </Tabs>
            </div>
          </div>
        )}

        {studyQuery.data && isEdit && (
          <EditStudy
            study={studyQuery.data.study}
            onClose={() => setIsEdit(false)}
            onSave={() => handleEdit()}
          />
        )}
        <Outlet context={{ container }} />
      </Drawer.Content>
    </Drawer>
  );
};

export const useStudyDetailsContext = () => {
  return useOutletContext<OutletContextType>();
};
